/** biome-ignore-all lint/suspicious/noExplicitAny: OpenSearch filter clauses are untyped */
import { SEARCH_CONFIG } from "@/config/searchConfig";
import type { Coords } from "@/types/places";
import {
  assertValidCoordinates,
  isValidCoordinate,
} from "@/utils/coordinateValidation";
import type { NextRequest } from "next/server";
import { hasLongWords } from "./sanitizeSearchQuery";

export interface MapBounds {
  topLeft: { lat: number; lng: number };
  bottomRight: { lat: number; lng: number };
}

export class InvalidBoundsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidBoundsError";
  }
}

/**
 * Validates that map bounds are geometrically valid and within reasonable limits
 */
export function validateBounds(bounds: MapBounds): void {
  const { topLeft, bottomRight } = bounds;

  // Use unified coordinate validation for individual corners
  try {
    assertValidCoordinates(topLeft.lat, topLeft.lng);
    assertValidCoordinates(bottomRight.lat, bottomRight.lng);
  } catch (error) {
    throw new InvalidBoundsError(
      error instanceof Error ? error.message : "Bounds contain invalid numbers",
    );
  }

  // Validate that topLeft is actually north of bottomRight
  if (topLeft.lat <= bottomRight.lat) {
    throw new InvalidBoundsError(
      "Top latitude must be greater than bottom latitude",
    );
  }

  // Note: We don't validate left < right for longitude because bounds can cross
  // the International Date Line (e.g., lng goes from 170 to -170)

  // Validate reasonable bounds size (not too small, not too large)
  const latDiff = Math.abs(topLeft.lat - bottomRight.lat);
  const lngDiff = Math.abs(topLeft.lng - bottomRight.lng);

  if (latDiff < 0.0001 || lngDiff < 0.0001) {
    throw new InvalidBoundsError("Bounds are too small");
  }

  if (latDiff > 180 || lngDiff > 360) {
    throw new InvalidBoundsError("Bounds are too large");
  }
}

export function parseBoundsFromRequest(request: NextRequest): MapBounds {
  const searchParams = request.nextUrl.searchParams;

  const bounds: MapBounds = {
    topLeft: {
      lat: Number.parseFloat(searchParams.get("topLeftLat") || "0"),
      lng: Number.parseFloat(searchParams.get("topLeftLng") || "0"),
    },
    bottomRight: {
      lat: Number.parseFloat(searchParams.get("bottomRightLat") || "0"),
      lng: Number.parseFloat(searchParams.get("bottomRightLng") || "0"),
    },
  };

  // Validate the parsed bounds
  validateBounds(bounds);

  return bounds;
}

/** Handles bounds across the date line (longitude wraps at ±180°). */
export function isWithinBounds(
  { lat, lon }: Coords,
  { topLeft, bottomRight }: MapBounds,
): boolean {
  if (lat > topLeft.lat || lat < bottomRight.lat) return false;
  return topLeft.lng > bottomRight.lng
    ? lon >= topLeft.lng || lon <= bottomRight.lng
    : lon >= topLeft.lng && lon <= bottomRight.lng;
}

export function boundsCenter(bounds: MapBounds): Coords {
  const west = bounds.topLeft.lng;
  // Bounds that cross the date line have west > east.
  const east =
    bounds.bottomRight.lng < west
      ? bounds.bottomRight.lng + 360
      : bounds.bottomRight.lng;
  const lon = (west + east) / 2;
  return {
    lat: (bounds.topLeft.lat + bounds.bottomRight.lat) / 2,
    lon: lon > 180 ? lon - 360 : lon,
  };
}

export function parseCommonParams(request: NextRequest): {
  size: number;
  from: number;
  origin: Coords | null;
} {
  const searchParams = request.nextUrl.searchParams;

  const { size: sizeLimits, offset: offsetLimits } = SEARCH_CONFIG.limits;
  const readInt = (key: string, fallback: number) => {
    const value = Number.parseInt(searchParams.get(key) ?? "", 10);
    return Number.isNaN(value) ? fallback : value;
  };

  const from = Math.max(0, Math.min(readInt("from", 0), offsetLimits.max - 1));
  // Size 0 asks for the total only. A page may not end past max_result_window.
  const size = Math.max(
    0,
    Math.min(
      readInt("size", sizeLimits.mapDefault),
      sizeLimits.mapMax,
      offsetLimits.max - from,
    ),
  );

  const origin = {
    lat: Number.parseFloat(searchParams.get("originLat") ?? ""),
    lon: Number.parseFloat(searchParams.get("originLng") ?? ""),
  };

  return {
    size,
    from,
    origin: isValidCoordinate(origin) ? origin : null,
  };
}

export function boundingBox(bounds: MapBounds): Record<string, unknown> {
  return {
    geo_bounding_box: {
      location: {
        top_left: { lat: bounds.topLeft.lat, lon: bounds.topLeft.lng },
        bottom_right: {
          lat: bounds.bottomRight.lat,
          lon: bounds.bottomRight.lng,
        },
      },
    },
  };
}

export function replaceGeoDistanceWithBoundingBox(
  baseFilters: Record<string, unknown>[],
  bounds: MapBounds,
): Record<string, unknown>[] {
  // Remove geo_distance filters
  const filters = baseFilters.filter((filter: any) => !filter.geo_distance);
  filters.push(boundingBox(bounds));
  return filters;
}

/**
 * Build OpenSearch query with search support.
 * Uses multi_match for safe, injection-proof search.
 *
 * @param filters - Base filters to apply
 * @param searchQuery - User's search query
 * @param fuzzyFields - Fields that support fuzzy matching; the first is the name field
 * @param exactFields - Fields that require exact matching (location, activities)
 */
export function buildQueryWithSearch(
  filters: Record<string, unknown>[],
  searchQuery?: string,
  fuzzyFields: string[] = ["name^3", "description"],
  exactFields: string[] = [],
) {
  if (!searchQuery?.trim()) {
    return { bool: { filter: filters } };
  }

  // Disable fuzziness for queries with long words to prevent DoS
  // Also disable for very short queries (1-2 chars)
  const shouldUseFuzziness =
    !hasLongWords(searchQuery) && searchQuery.trim().length >= 3;

  const fuzzyClause: any = {
    multi_match: {
      query: searchQuery,
      fields: fuzzyFields,
      type: "best_fields",
    },
  };

  // Only add fuzziness if query doesn't contain long words
  if (shouldUseFuzziness) {
    fuzzyClause.multi_match.fuzziness = "AUTO";
  }

  const shouldClauses = [fuzzyClause];

  // Add prefix matching for partial word search (e.g., "bad" matches "badminton")
  // Only applied to name field where prefix matches are most valuable
  const [nameField] = fuzzyFields;
  if (shouldUseFuzziness && nameField) {
    shouldClauses.push({
      multi_match: {
        query: searchQuery,
        fields: [nameField],
        type: "phrase_prefix",
        boost: 1.5,
        max_expansions: 50,
      },
    });
  }

  // Add exact matching clause if exactFields provided
  if (exactFields.length > 0) {
    shouldClauses.push({
      // Exact matching for structured data (no fuzzy to avoid garbage results)
      multi_match: {
        query: searchQuery,
        fields: exactFields,
        type: "best_fields",
      },
    });
  }

  return {
    bool: {
      should: shouldClauses,
      minimum_should_match: 1,
      filter: filters,
    },
  };
}

export type SortClauses = Array<Record<string, unknown>> | undefined;

export function isListView(searchParams: URLSearchParams): boolean {
  return searchParams.get("view") === "list";
}

/**
 * Both the map pins and the viewport list read these routes. The map wants a
 * stable sample of pins; the list (`view=list`) wants the visitor's chosen sort.
 */
export function resolveSort(
  searchParams: URLSearchParams,
  sorts: { list: SortClauses; map: SortClauses },
): SortClauses {
  return isListView(searchParams) ? sorts.list : sorts.map;
}
